import { emptyToNull } from '@utils/string'

/**
 * Shape expected by address formatting utilities.
 * Compatible with AddressModel, Address, and similar shapes.
 */
export interface AddressFields {
  streetName?: string | null
  houseNumber?: string | null
  postalCode?: string | null
  city?: string | null
  country?: string | null
}

/**
 * Formats an address for display.
 * Combines street parts (streetName + houseNumber) and location parts (postalCode + city).
 *
 * @param address - The address fields to format
 * @param fallback - Fallback string when address is empty (default: '-')
 * @returns Formatted address string or fallback
 *
 * @example
 * formatAddressDisplay({ streetName: 'Main St', houseNumber: '42', postalCode: '0123', city: 'Oslo' })
 * // Returns: 'Main St 42, 0123 Oslo'
 */
export function formatAddressDisplay(address?: AddressFields | null, fallback = '-'): string {
  if (!address) {
    return fallback
  }

  const street = [address.streetName, address.houseNumber].filter(Boolean).join(' ')
  const city = [address.postalCode, address.city].filter(Boolean).join(' ')
  const values = [street, city].filter(Boolean)

  return values.length > 0 ? values.join(', ') : fallback
}

interface MailAddressNormalizable {
  careOf?: string | null
  name?: string | null
}

export function normalizeMailAddress<T extends MailAddressNormalizable>(
  mailAddress: T | undefined | null,
): T | undefined {
  if (!mailAddress) return undefined
  return {
    ...mailAddress,
    careOf: emptyToNull(mailAddress.careOf),
    name: emptyToNull(mailAddress.name),
  }
}
